import { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Lightbulb, ChevronUp, Plus } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  new: { label: 'Open for votes', color: '#5B7C99' },
  considering: { label: 'Being considered', color: '#E8C265' },
  written: { label: 'Written ✓', color: '#78A18E' },
};

export default function Topics() {
  const { user } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/topics');
      setTopics(data);
    } catch {} finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    if (!user) { toast.error('Please sign in to suggest a topic'); return; }
    setSubmitting(true);
    try {
      await api.post('/topics', { title: title.trim(), description: desc.trim() || null });
      toast.success('Thank you — your idea is on the board!');
      setTitle(''); setDesc(''); setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  const toggleUpvote = async (id) => {
    if (!user) { toast.error('Please sign in to upvote'); return; }
    try {
      const { data } = await api.post(`/topics/${id}/upvote`);
      setTopics((prev) => prev.map((t) => t.id === id ? { ...t, upvote_count: data.upvote_count, user_has_upvoted: data.user_has_upvoted } : t));
    } catch { toast.error('Could not save upvote'); }
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'saturate(0.9) brightness(1.05)' }} aria-hidden />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(253, 251, 247, 0.94) 0%, rgba(243, 239, 231, 0.9) 50%, rgba(253, 251, 247, 0.85) 100%)' }} aria-hidden />
        <div className="max-w-4xl mx-auto px-6 py-20 text-center relative">
          <div className="inline-flex items-center gap-3 mb-4">
            <Lightbulb className="w-8 h-8" style={{ color: '#E57A44', strokeWidth: 1.5 }} />
            <h1 className="text-4xl sm:text-5xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }} data-testid="topics-title">
              Topic Ideas Board
            </h1>
          </div>
          <p className="text-lg mb-6" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            What should Genesis write about next? Suggest a topic, upvote the ones you'd love to read, and watch what she publishes.
          </p>
          <button
            onClick={() => { if (!user) { toast.error('Please sign in to suggest a topic'); return; } setShowForm(v => !v); }}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full transition-all duration-300 hover:-translate-y-1"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
            data-testid="topics-open-form"
          >
            <Plus className="w-4 h-4" />
            <span>Suggest a Topic</span>
          </button>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-12">
        {showForm && user && (
          <form onSubmit={submit} className="p-8 rounded-3xl mb-8 space-y-4" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.05)' }} data-testid="topics-form">
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Topic Title</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140} required
                placeholder="e.g., How to trust again after betrayal"
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="topics-form-title" />
            </div>
            <div>
              <label className="block mb-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500 }}>Why does this matter to you? <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
              <textarea value={desc} onChange={(e) => setDesc(e.target.value)} maxLength={800} rows={3}
                placeholder="A short note to help Genesis understand what would speak to you…"
                className="w-full px-4 py-3 rounded-xl border"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="topics-form-desc" />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2 rounded-full" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#F3EFE7', color: '#595553', fontWeight: 600 }}>Cancel</button>
              <button type="submit" disabled={submitting || !title.trim()} className="px-6 py-2 rounded-full disabled:opacity-50" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }} data-testid="topics-form-submit">
                {submitting ? 'Sharing…' : 'Share Idea'}
              </button>
            </div>
          </form>
        )}

        {!user && showForm && (
          <div className="p-6 rounded-3xl mb-8 text-center" style={{ backgroundColor: '#F3EFE7' }}>
            <p className="mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Sign in to suggest a topic — helps keep the board thoughtful.</p>
            <Link to="/login" className="inline-block px-6 py-2 rounded-full" style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}>Sign In</Link>
          </div>
        )}

        {loading ? (
          <p className="text-center py-12" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>Loading ideas…</p>
        ) : topics.length === 0 ? (
          <div className="text-center py-16 rounded-3xl" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 8px 30px rgba(0,0,0,0.04)' }} data-testid="topics-empty">
            <Lightbulb className="w-12 h-12 mx-auto mb-4" style={{ color: '#E57A44' }} />
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>The board is empty. Be the first to suggest a topic.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="topics-list">
            {topics.map((t) => {
              const meta = STATUS_META[t.status] || STATUS_META.new;
              return (
                <div key={t.id} className="p-5 rounded-2xl flex items-start gap-4" style={{ backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }} data-testid={`topic-${t.id}`}>
                  <button
                    onClick={() => toggleUpvote(t.id)}
                    className="flex flex-col items-center justify-center rounded-2xl p-3 min-w-[64px] transition-all duration-200 hover:scale-105"
                    style={{
                      fontFamily: 'Nunito, sans-serif',
                      backgroundColor: t.user_has_upvoted ? '#E57A44' : '#F3EFE7',
                      color: t.user_has_upvoted ? '#FDFBF7' : '#595553',
                      fontWeight: 700
                    }}
                    data-testid={`topic-upvote-${t.id}`}
                  >
                    <ChevronUp className="w-5 h-5" strokeWidth={2.5} />
                    <span>{t.upvote_count}</span>
                  </button>
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{t.title}</h3>
                      <span className="px-3 py-0.5 rounded-full text-xs uppercase tracking-wide font-semibold" style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: `${meta.color}22`, color: meta.color }}>{meta.label}</span>
                    </div>
                    {t.description && <p className="text-sm mb-2" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.6 }}>{t.description}</p>}
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>— {t.author_name} · {new Date(t.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
